<script lang="ts">
  import type { FPost, IUser, WithCreatorData } from 'garden-common';
  
  import { createEventDispatcher, onMount } from 'svelte';
  import { me } from '$stores/user.store';
  import { fly } from 'svelte/transition';

  import LargeTextInput from '../../common/inputs/textInputs/LargeTextInput.svelte';
  import { DropdownItem } from '../../common/dropdowns/dropdown';
  import Dropdown from '$lib/components/common/dropdowns/Dropdown.svelte';
  import Button from '../../common/buttons/Button.svelte';
  import Main from '../../common/inputs/textInputs/GlobalTextInput.svelte';
  import Modal from '../SmallModal/Modal.svelte';

  import KeyboardHint from '$icons/KeyboardHintIcon.svelte';
  import AddSquare from '$icons/AddSquareIcon.svelte';
  import Note from '$icons/NoteIcon.svelte';
  import Loader from '$icons/LoaderIcon.svelte';
  
  import { findPosts } from '$src/utils/api/posts.util';
  import { getAllUsers } from '$utils/api/user.util';
  import { createGallery, type GalleryVisibility } from '$src/utils/api/gallery.util';

  export let post: Partial<FPost<string>> & Partial<WithCreatorData> = null;

  const dispatch = createEventDispatcher();

  function closeModal() {
    dispatch('ModalClose');
  }

  function closeModalSuccess(gallery) {
    dispatch('GalleryCreated', { gallery });
  }

  const privacyDropdownItem: DropdownItem<never>[] = [
    { label: 'Everyone', val: 'everyone' },
    { label: 'Followers', val: 'followers' },
    // { label: 'Talents', val: 'talents' },
    { label: 'Private', val: 'private' }
  ].map(e => new DropdownItem({ variant: 'label', id: e.val, data: { label: e.label } }));

  let selectedVisibilityItem: DropdownItem<never> = privacyDropdownItem[2];

  let selectedVisibility: GalleryVisibility;
  $: selectedVisibility = selectedVisibilityItem?.id as GalleryVisibility;

  let title = '';
  let description = '';

  let collaboratorDropdownItems: DropdownItem<never>[] = [];
  let postsDropdownItems: DropdownItem<never>[] = [];

  let query = '';
  let postQuery = '';

  async function searchForUsers() {
    if (!query) return;

    const res = (await getAllUsers(10, 0, {
      userName: { $regex: `^${query}.*`, $options: 'i' }
    })) as IUser<string>[];

    if (!res || !res.length) return;

    collaboratorDropdownItems = res.map(
      e =>
        new DropdownItem({
          variant: 'label,secondaryLabel,imageOrIcon',
          id: e._id,
          data: {
            label: e.userName,
            secondaryLabel: e.name,
            imageUrl: e.profileImage,
            altimageUrl: `https://api.dicebear.com/6.x/thumbs/svg?backgroundColor=B5F1D4&shapeColor=074D2B&seed=${e.userName}`
          }
        })
    );
  }

  async function searchForPosts() {
    if (!postQuery) return;

    const res = await findPosts(10, 0, postQuery);

    if (!res || !res.length) return;

    postsDropdownItems = res.map(
      e =>
        new DropdownItem({
          variant: 'label,secondaryLabel,imageOrIconPosts',
          id: e._id,
          data: {
            label: e.title,
            secondaryLabel: e.description,
            imageUrl: e.media[0].images[0]
          }
        })
    );
  }

  let selectedCollaboratorItems: DropdownItem<never>[] = [];
  let selectedCollaborators: IUser<string>['_id'][] = [];

  let selectedPostItems: DropdownItem<never>[] = [];
  let selectedPosts: FPost<string>['_id'][] = [];

  $: selectedCollaborators = selectedCollaboratorItems.map(item => item.id);
  $: selectedPosts = selectedPostItems.map(item => item.id);

  let isCreatingGallery = false;
  let error: string = '';

  async function nextCreateGallery() {
    if (!$me) return;
    isCreatingGallery = true;

    const res = await createGallery(
      $me._id,
      selectedVisibility,
      title,
      description,
      selectedCollaborators,
      selectedPosts
    ).catch(e => {
      error = e.response.data.message;
      console.error(e);
    });

    if (error || !res) {
      isCreatingGallery = false;
      return;
    }

    closeModalSuccess(res);
  }

  $: isInputValid = !!title;

  $: collaboratorDropdownDanger = error === "The creator can't be a collaborator";

  onMount(() => {
    if (!post) return;

    selectedPostItems.push(
      new DropdownItem({
        variant: 'label,secondaryLabel,imageOrIconPosts',
        id: post._id,
        data: {
          label: post.title,
          secondaryLabel: post.description,
          imageUrl: post.media[0].images[0]
        }
      })
    );
  });
</script>

<Modal contentOverflow class="h-[800px]" on:ModalClose={closeModal}>
  <!-- Header -->
  <div slot="header" class="flex flex-col items-center gap-8 mt-12 mb-6">
    <Note class="stroke-brand-green" size={'64'} />

    <div class="flex flex-col items-center gap-2">
      <h1 class="text-2xl font-bold">Create your gallery</h1>

      <h2 class="text-grey-transparency-dark text-opacity-40 text-sm max-w-[18rem] text-center">
        Create galleries to curate and showcase your collection of posts and people.
      </h2>
    </div>
  </div>

  <!-- Content -->
  <div
    slot="content"
    class="md:w-[600px] overflow-y-scroll h-full px-4 py-2 border border-blueish-grey-200 rounded-2xl"
    data-simplebar
    data-simplebar-auto-hide="false"
  >
    <div class=" flex flex-col gap-1 mb-5">
      <div class="flex items-center gap-2">
        <span class="text-[#000F27] font-medium">This gallery will be visible to </span>

        <Dropdown
          bind:selected={selectedVisibilityItem}
          variant="selected-label-with-arrow"
          items={privacyDropdownItem}
          variantOptions={{
            extLabelClasses: '!text-brand-green font-medium',
            arrowColor: '#47675B',
            variant: 'selected-label-with-arrow'
          }}
        />
      </div>

      <span class="text-grey-transparency-dark text-opacity-40 text-xs">
        Choose the audience who can view your gallery
      </span>
    </div>

    <Main bind:value={title} placeholder="Enter title here" label="Title" />

    <LargeTextInput
      bind:value={description}
      placeholder="Enter a description for your gallery."
      label="Description (Optional)"
    />

    <div class="z-[1000]">
      <Dropdown
        bind:query
        bind:selectedItems={selectedCollaboratorItems}
        on:stopTyping={searchForUsers}
        variant="mul-tag-search"
        items={collaboratorDropdownItems || []}
        caption="Add collaborators that can help you with curating items to this gallery"
        danger={collaboratorDropdownDanger}
        variantOptions={{
          arrow: true,
          variant: 'mul-tag-search',
          arrowColor: '#47675B',
          arrowSize: '24'
        }}
      />
    </div>

    <div class="z-[1000]">
      <Dropdown
        bind:query={postQuery}
        bind:selectedItems={selectedPostItems}
        on:stopTyping={searchForPosts}
        variant="mul-tag-search"
        items={postsDropdownItems || []}
        caption="Add posts that will be featured in the gallery"
        variantOptions={{
          arrow: true,
          variant: 'mul-tag-search',
          arrowColor: '#47675B',
          arrowSize: '24'
        }}
      />
    </div>

    <div class="">
      {#if error}
        <div class="w-full text-color-red-light">
          {error}
        </div>
      {/if}
    </div>
  </div>

  <!-- Footer -->
  <div slot="footer">
    <div class="flex items-center gap-2 mt-4">
      <div class="flex-grow" />

      <div class="flex items-center gap-2">
        <Button secondary clickOnKey="Escape" label="Cancel" on:click={closeModal}>
          <div slot="icon-right">
            <KeyboardHint type="escape" />
          </div>
        </Button>

        <div class="relative grid place-items-center">
          <Button
            primary
            clickOnKey="Enter"
            border={false}
            label="Create gallery"
            on:click={nextCreateGallery}
            disabled={!isInputValid || isCreatingGallery}
          >
            <div slot="icon-left">
              <AddSquare class="stroke-white" />
            </div>
          </Button>
        </div>

        {#if isCreatingGallery}
          <div in:fly|global={{ x: 20 }}>
            <Loader />
          </div>
        {/if}
      </div>
    </div>
  </div>
</Modal>

<style lang="postcss">
  :global(.simplebar-scrollbar::before) {
    @apply my-2 translate-x-[5px];
    background: #d0ddd7 !important;
    width: 4px;
  }

  :global(.simplebar-content-wrapper) {
    @apply overscroll-contain;
  }
</style>
